<template>
  <div class="projects__nav" @click.stop.prevent="">
    <LinkIconOnly
      v-if="project.status.reportCreated"
      class="projects__nav-item"
      icon="report"
      :id="`${project.id}-report`"
      @click="navigateTo(`/project/${project.id}/report/${project.report || 'empty'}`)"
    >
      <VisibleText>View</VisibleText> {{ project.name }}
      <VisibleText> report</VisibleText>
    </LinkIconOnly>
    <LinkIconOnly
      class="projects__nav-item"
      :href="lastOpenedTab"
      :id="`${project.id}-edit`"
      icon="edit"
      @click="navigateTo(lastOpenedTab)"
    >
      <span><VisibleText>Edit </VisibleText>{{ project.name }}</span>
    </LinkIconOnly>

    <LinkIconOnly
      class="projects__nav-item"
      icon="share"
      :href="`/project/${project.id}?share=true`"
      :id="`${project.id}-share`"
      v-if="isOwner"
      @click="navigateTo(`/project/${project.id}?share=true`)"
    >
      <VisibleText>Share </VisibleText>{{ project.name }}
    </LinkIconOnly>

    <ButtonIconOnly
      class="projects__nav-item"
      icon="deleteIcon"
      style="padding-top: 0.25rem"
      :id="`${project.id}-del`"
      @click-handler="confirmDelete"
      v-if="isOwner"
    >
      <VisibleText>Delete </VisibleText>{{ project.name }}
    </ButtonIconOnly>
  </div>
</template>

<script>
import ButtonIconOnly from '@/components/UI/Button/ButtonIconOnly.vue';
import LinkIconOnly from '@/components/UI/Link/LinkIconOnly.vue';
import { PigeonDocCollectionModel, PigeonDocModel } from '@/pigeondoc/wrapper';
import { fetchProject } from '@/components/Project/Utils/fetch.js';
import TextService from '@/services/textService.js';

import BenchmarkingProject from '@/models/BenchmarkingProjectModel.js';

export default {
  name: 'ProjectsItemNav',
  components: {
    ButtonIconOnly,
    LinkIconOnly,
  },
  data() {
    return {
      TEXT_SERVICE: new TextService(this.$pigeonline),
      BenchmarkingProject: BenchmarkingProject,
    };
  },
  props: {
    project: {
      default: () => {},
      type: BenchmarkingProject,
    },
    setToEdit: {
      default: () => false,
      type: Boolean,
    },
    setToReport: {
      default: () => false,
      type: Boolean,
    },
    setToShare: {
      default: () => false,
      type: Boolean,
    },
  },
  computed: {
    confirmStatus() {
      return this.$store.getters['confirm/getStatus'];
    },
    datasetsParams() {
      return this.project.datasets ? `id: ${this.project.datasets[0]}` : '';
    },
    isOwner() {
      return this.project.owner == this.$store.getters['user/getProfile'].django_ref_id;
    },
    lastOpenedTab() {
      if (this.project.settings) {
        if (this.project.settings.lastOpenedTab) {
          return '/project/' + this.project.id + '/analysis/' + this.project.settings.lastOpenedTab;
        }
      }
      return '/project/' + this.project.id;
    },
  },
  methods: {
    navigateTo(url) {
      if (url) {
        this.$router.push(url);
      }
    },
    confirmDelete() {
      this.$store.dispatch('confirm/setConfirmTarget', this.project.id);
      this.$store.dispatch('confirm/setConfirmIsVisible', true);
      this.$store.dispatch('confirm/setConfirmText', {
        btn: 'Delete Project',
        message: 'This will also delete your report.',
        title: 'Delete project?',
      });
      this.$store.dispatch('confirm/setConfirmType', 'delete');
      this.$store.dispatch('confirm/setConfirmSourceComponent', 'projects');
    },
    async deleteProject() {
      try {
        // Check for related themes inside any textAnalysisObjects to prevent zombie themes
        if (this.project.textAnalysisObjects > 0) {
          const fullProject = await fetchProject(this.project.id);

          if (fullProject.textAnalysisObjects && fullProject.textAnalysisObjects.length) {
            for (let a = 0; a < fullProject.textAnalysisObjects.length; a++) {
              let payload = {
                text_analysis_uuid: fullProject.textAnalysisObjects[a].uuid,
              };
              const fetchedThemes = await this.TEXT_SERVICE.fetchTextThemes(payload);
              const all_themes = fetchedThemes.map((theme) =>
                theme._id.$oid ? theme._id.$oid : theme._id,
              );
              if (all_themes.length) {
                let payload = {
                  _ids: all_themes,
                  text_analysis_uuid: fullProject.textAnalysisObjects[a].uuid,
                };
                await this.TEXT_SERVICE.deleteTextThemes(payload);
              }
            }
          }
        }

        if (this.project.collection_id) {
          let collection = await this.$pigeonline.projects.getByID(
            PigeonDocCollectionModel,
            this.project.collection_id,
          );
          if (collection && collection.id) {
            if (collection.docIds) {
              for (let i = 0; i < collection.docIds.length; i++) {
                let collection_report = await this.$pigeonline.projects.getByID(
                  PigeonDocModel,
                  collection.docIds[i],
                );
                if (collection_report && collection_report.id) {
                  await this.$pigeonline.projects.delete(collection.docIds[i]);
                }
              }
            }
            await this.$pigeonline.projects.delete(this.project.collection_id);
          }
        }
        if (this.project.report) {
          let report = await this.$pigeonline.projects.getByID(
            BenchmarkingProject,
            this.project.report,
          );
          if (report && report.id) {
            await this.$pigeonline.projects.delete(this.project.report);
          }
        }

        await this.$pigeonline.projects.delete(this.project.id);
        // TODO: reload only the past projects component
        this.$router.go();
      } catch (e) {
        throw new Error('ProjectList:del ' + e.message);
      }
    },
  },
  watch: {
    confirmStatus: async function (val) {
      const targetProject = this.$store.getters['confirm/getTarget'];
      const sourceComponent = this.$store.getters['confirm/getSourceComponent'];
      if (val && targetProject === this.project.id && sourceComponent === 'projects') {
        await this.deleteProject();
      }
    },
    setToEdit: async function (val) {
      if (val) {
        if (this.lastOpenedTab) {
          this.$router.push(this.lastOpenedTab);
        }
      }
    },
    setToReport: async function (val) {
      if (val) {
        let url = 'empty';
        if (this.project.report) {
          url = '/project/' + this.project.id + '/report/' + this.project.report;
        }
        this.$router.push(url);
      }
    },
    setToShare: async function (val) {
      if (val) {
        let url = `/project/${this.project.id}?share=true`;
        this.$router.push(url);
      }
    },
  },
};
</script>
